import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import ReplayIcon from '@mui/icons-material/Replay';
import DataTable, { type DataTableColumn } from '@shared/components/DataTable';
import CardSection from '@shared/components/CardSection';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import { useTransfer } from '../hooks/useTransfer';
import { useRetryTarget } from '../hooks/useRetryTarget';
import type { TransferTarget, TransferTargetStatus } from '../types';
import type { OperationStatus } from '@shared/types';

const statusColorMap: Record<
  OperationStatus,
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  queued: 'default',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  partial: 'warning',
  cancelled: 'warning',
};

const targetStatusColorMap: Record<TransferTargetStatus, 'default' | 'info' | 'success' | 'error'> =
  {
    pending: 'default',
    running: 'info',
    succeeded: 'success',
    failed: 'error',
  };

function formatBytes(bytes?: number): string {
  if (bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

interface Props {
  operationId: string;
}

export default function TransferStatus({ operationId }: Props) {
  const { data: transfer, isLoading } = useTransfer(operationId);
  const retryTarget = useRetryTarget();

  if (isLoading || !transfer) return <PanelSkeleton />;

  const columns: DataTableColumn<TransferTarget>[] = [
    {
      id: 'targetName',
      label: 'Target',
      width: 200,
      render: (row) => row.targetName,
    },
    {
      id: 'status',
      label: 'Status',
      width: 100,
      render: (row) => (
        <Chip
          label={row.status}
          color={targetStatusColorMap[row.status]}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      id: 'progress',
      label: 'Progress',
      width: 150,
      render: (row) => {
        if (row.status === 'running' || row.status === 'pending') {
          return (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant={row.status === 'pending' ? 'indeterminate' : 'determinate'}
                value={row.progressPercent ?? 0}
              />
            </Box>
          );
        }
        return `${row.progressPercent ?? 0}%`;
      },
    },
    {
      id: 'size',
      label: 'Transferred',
      width: 130,
      render: (row) =>
        row.bytesPlanned
          ? `${formatBytes(row.bytesCopied)} / ${formatBytes(row.bytesPlanned)}`
          : '—',
    },
    {
      id: 'actions',
      label: '',
      width: 80,
      render: (row) =>
        row.status === 'failed' ? (
          <Button
            size="small"
            startIcon={<ReplayIcon />}
            onClick={() =>
              retryTarget.mutate({ operationId: transfer.operationId, targetId: row.targetId })
            }
            disabled={retryTarget.isPending}
            sx={{ textTransform: 'none', minWidth: 0 }}
          >
            Retry
          </Button>
        ) : null,
    },
  ];

  return (
    <div data-testid="transfer-status">
      <CardSection title="Transfer Progress">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body2">
            Operation: <strong>{transfer.operationId}</strong>
          </Typography>
          <Chip
            label={transfer.status}
            color={statusColorMap[transfer.status]}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
          <LinearProgress variant="determinate" value={transfer.progressPercent} sx={{ flex: 1 }} />
          <Typography variant="body2">{transfer.progressPercent}%</Typography>
        </Box>

        {transfer.status === 'partial' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Transfer partially completed. Some targets failed — retry available.
          </Alert>
        )}

        {transfer.status === 'failed' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Transfer failed. Retry failed targets below.
          </Alert>
        )}

        <DataTable
          columns={columns}
          rows={transfer.targets}
          rowKey={(row) => row.targetId}
          emptyMessage="No targets"
        />
      </CardSection>
    </div>
  );
}
